import React, { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Grid,
  Chip,
  CircularProgress,
  Autocomplete,
  FormControlLabel,
  Switch,
  Alert,
  Typography,
} from '@mui/material';
import {
  GridColDef,
  GridSortModel,
  GridPaginationModel,
  GridRowParams,
  GridColumnVisibilityModel,
  GridToolbarContainer,
  GridPagination,
  GridToolbarColumnsButton,
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import { AxiosResponse } from 'axios';
import axios from '../../../utils/axios';
import { API_URL, PAGE_SIZE } from '../../../constants';
import { PageWrapper } from '../../../styledComponents/wrappers';
import { StripedDataGrid } from '../../../styledComponents/datagrid';
import { Button, TextField } from '../../../components/library';
import {
  capitalizeFirstletter,
  FormatDateString,
  FormatDateTimeString,
} from '../../../utils/helper';
import { getZoneNameFromZone } from '../../catalog/utils/inventory/utils';
import { formatUserName } from '../utils/userUtils';
import { renderExports } from '../utils/exportUtils';
import {
  InventoryTransfer,
  WarehouseZone as UtilsWarehouseZone,
} from '../../../services/inventory/types';
import { renderCellExpand } from '../../../components/library/DataGrid/renderCellExpand';
import { useKeyPress } from '../../../utils/hooks/useKeyPress';

interface WarehouseZone extends UtilsWarehouseZone {
  nsLocationId: number;
}

interface SearchFilters {
  searchTerm: string;
  fromWarehouseZoneId: number | null;
  toWarehouseZoneId: number | null;
  skuFilter: string[];
  dateFrom: string;
  dateTo: string;
}

export const INVENTORY_TRANSFERS_ROUTE = '/inventory/transfers';

const InventoryTransfersList: React.FC = () => {
  const [searchInitiated, setSearchInitiated] = useState<boolean>(false);
  const [transfers, setTransfers] = useState<InventoryTransfer[]>([]);
  const [warehouseZones, setWarehouseZones] = useState<WarehouseZone[]>([]);
  const [resultCount, setResultCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZE,
  });
  const [filters, setFilters] = useState<SearchFilters>({
    searchTerm: '',
    fromWarehouseZoneId: null,
    toWarehouseZoneId: null,
    skuFilter: [],
    dateFrom: '',
    dateTo: '',
  });
  const [skuFilterInput, setSkuFilterInput] = useState<string>('');
  const [storeSearch, setStoreSearch] = useState<boolean>(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({});

  const history = useHistory();

  const columns: GridColDef[] = [
    {
      headerName: 'ID',
      field: 'id',
      flex: 0.5,
    },
    {
      headerName: 'Transfer Type',
      field: 'transferType',
      flex: 1,
      renderCell: params => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'zone' ? 'primary' : 'secondary'}
          variant="outlined"
        />
      ),
    },
    {
      headerName: 'From Zone',
      field: 'fromWarehouseZone',
      flex: 1.5,
      renderCell: params => getZoneNameFromZone(params.value) || 'N/A',
    },
    {
      headerName: 'To Zone',
      field: 'toWarehouseZone',
      flex: 1.5,
      renderCell: params => getZoneNameFromZone(params.value) || 'N/A',
    },
    {
      headerName: 'Items Count',
      field: 'inventoryTransferItems',
      flex: 1,
      renderCell: params => params.value?.length || 0,
    },
    {
      headerName: 'Exports',
      field: 'exports',
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const exports = params.row.inventoryTransferExports || [];
        const exportContent = renderExports(exports);
        params.value = exportContent;
        return renderCellExpand(params);
      },
    },
    {
      headerName: 'Created By',
      field: 'createdByUser',
      valueGetter: (params: GridValueGetterParams) =>
        formatUserName(params.row.createdByUser, params.row.createdByUserId),
      renderCell: (params: GridRenderCellParams) => {
        const user = params.row.createdByUser;
        if (!user) {
          params.value = `User ${params.row.createdByUserId}`;
          return renderCellExpand(params);
        }

        const displayName = user.name || user.email;
        const hoverValue = `${displayName} (${user.email})`;
        params.value = formatUserName(user, params.row.createdByUserId);
        return renderCellExpand(params, hoverValue);
      },
      flex: 1.5,
    },
    {
      headerName: 'Created',
      field: 'created',
      renderCell: (params: GridRenderCellParams) => {
        const hoverValue = FormatDateTimeString(params.value);
        params.value = FormatDateString(params.value);
        return renderCellExpand(params, hoverValue);
      },
      flex: 1,
    },
  ];

  const fetchWarehouseZones = async () => {
    try {
      const response = await axios.get(`${API_URL}/inventory/warehouse-zones`);
      setWarehouseZones((response as AxiosResponse<WarehouseZone[]>).data);
    } catch (error) {
      console.error('Error fetching warehouse zones:', error);
    }
  };

  const handleApiError = (error: any, context: string) => {
    console.error(`Error ${context}:`, error);

    if (error.response?.status === 414 || error.message?.includes('URI too long')) {
      setError(
        'The search parameters are too long. Please reduce the number of SKUs in your filter.',
      );
    } else if (error.response?.status === 400) {
      setError('Invalid search parameters. Please check your filters and try again.');
    } else if (error.response?.status >= 500) {
      setError('Server error occurred. Please try again later.');
    } else {
      setError(`An error occurred while ${context}. Please try again.`);
    }
  };

  const fetchData = useCallback(async () => {
    if (!searchInitiated) return;
    setLoading(true);
    setError(null);

    const sortBy = sortModel?.[0]?.field ?? 'created';
    const sortOrder = sortModel?.[0]?.sort?.toUpperCase() ?? 'DESC';

    try {
      const response = await axios.get(`${API_URL}/inventory/transfers`, {
        params: {
          page: paginationModel.page + 1,
          pageSize: paginationModel.pageSize,
          sort: sortBy,
          sortOrder,
          searchTerm: filters.searchTerm,
          fromWarehouseZoneId: filters.fromWarehouseZoneId,
          toWarehouseZoneId: filters.toWarehouseZoneId,
          skuFilter: filters.skuFilter.join(','),
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
        },
      });

      const { transfers, totalCount } = (response as AxiosResponse<{
        transfers: InventoryTransfer[];
        totalCount: number;
      }>).data;
      setTransfers(transfers);
      setResultCount(totalCount);
    } catch (error) {
      handleApiError(error, 'fetching inventory transfers');
    } finally {
      setLoading(false);
      setSearchInitiated(false);
    }
  }, [filters, paginationModel, sortModel, searchInitiated]);

  const onRowClick = (params: GridRowParams) => {
    const { id } = params;
    history.push(`${INVENTORY_TRANSFERS_ROUTE}/${id}`);
  };

  const generateCsvValue = (col: GridColDef, row: InventoryTransfer) => {
    let value = '';

    // Handle special cases for specific columns
    if (col.field === 'exports') {
      const exports = row.inventoryTransferExports || [];
      if (exports.length === 0) {
        value = '—';
      } else {
        value = exports.map(exp => capitalizeFirstletter(exp.exportType)).join(', ');
      }
    } else if (col.field === 'createdByUser') {
      const user = row.createdByUser;
      if (!user) {
        value = `User ${row.createdByUserId}`;
      } else {
        value = formatUserName(user, row.createdByUserId);
      }
    } else if (col.field === 'created') {
      value = FormatDateString(row.created);
    } else if (col.field === 'fromWarehouseZone') {
      value = getZoneNameFromZone(row.fromWarehouseZone || undefined) || 'N/A';
    } else if (col.field === 'toWarehouseZone') {
      value = getZoneNameFromZone(row.toWarehouseZone || undefined) || 'N/A';
    } else if (col.field === 'inventoryTransferItems') {
      value = String(row.inventoryTransferItems?.length || 0);
    } else if (col.field === 'transferType') {
      value = String(row.transferType || '');
    } else {
      // Default handling for other fields
      const fieldValue = row[col.field as keyof InventoryTransfer];
      value = String(fieldValue || '');
    }

    value = value.replace(/"/g, '""');
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      value = `"${value}"`;
    }
    return value;
  };

  const handleExport = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_URL}/inventory/transfers`, {
        params: {
          page: 1,
          pageSize: 10000,
          sort: 'created',
          sortOrder: 'DESC',
          searchTerm: filters.searchTerm,
          fromWarehouseZoneId: filters.fromWarehouseZoneId,
          toWarehouseZoneId: filters.toWarehouseZoneId,
          skuFilter: filters.skuFilter.join(','),
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
        },
      });

      const { transfers: allTransfers } = (response as AxiosResponse<{
        transfers: InventoryTransfer[];
        totalCount: number;
      }>).data;

      const visibleColumns = columns.filter(col => columnVisibilityModel[col.field] !== false);
      const headers = visibleColumns.map(col => col.headerName);
      const csvRows = [headers.join(',')];

      allTransfers.forEach(row => {
        const values = visibleColumns.map(col => generateCsvValue(col, row));
        csvRows.push(values.join(','));
      });

      const csv = csvRows.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `drivetrain-inventory-transfers-${
        new Date().toISOString().split('T')[0]
      }.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      handleApiError(error, 'exporting inventory transfers');
    } finally {
      setLoading(false);
    }
  };

  const processSkuFilter = (input: string) =>
    input
      ? input
          .split(',')
          .map(s => s.trim())
          .filter(s => s)
      : [];

  const handleFilterClear = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters({
      searchTerm: '',
      fromWarehouseZoneId: null,
      toWarehouseZoneId: null,
      skuFilter: [],
      dateFrom: '',
      dateTo: '',
    });
    setSkuFilterInput('');
  };

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({
      ...prev,
      skuFilter: processSkuFilter(skuFilterInput),
    }));
    setSearchInitiated(true);
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer sx={{ justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={handleExport}
          disabled={loading}
          size="small"
          ordinality="primary"
        >
          {loading ? 'Exporting...' : 'Export All'}
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GridToolbarColumnsButton />
          <GridPagination sx={{ mx: 1 }} />
        </Box>
      </GridToolbarContainer>
    );
  }

  function CustomPagination() {
    return (
      <GridToolbarContainer sx={{ justifyContent: 'right' }}>
        <GridPagination sx={{ mx: 4 }} />
      </GridToolbarContainer>
    );
  }

  useEffect(() => {
    fetchWarehouseZones();

    const storedSearch = window.localStorage.getItem('inventoryTransfersSearch');
    if (storedSearch) {
      setStoreSearch(true);
      const parsedFilters = JSON.parse(storedSearch);
      setFilters(parsedFilters);
      setSkuFilterInput(parsedFilters.skuFilter.join(', '));
      setSearchInitiated(true);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, paginationModel, sortModel, searchInitiated]);

  useEffect(() => {
    if (storeSearch) {
      window.localStorage.setItem('inventoryTransfersSearch', JSON.stringify(filters));
    } else {
      window.localStorage.removeItem('inventoryTransfersSearch');
    }
  }, [storeSearch, filters]);

  useEffect(() => {
    window.localStorage.setItem(
      'inventoryTransfersSearchColumns',
      JSON.stringify(columnVisibilityModel),
    );
  }, [columnVisibilityModel]);

  useKeyPress({ activationKey: 'enter' }, () => setSearchInitiated(true));

  return (
    <PageWrapper>
      <Typography variant="h4" gutterBottom>
        Inventory Transfers
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Grid container direction="row" spacing={2} justifyContent="start">
        <Grid container item xs={8} spacing={1}>
          <Grid container item xs={12} md={6} spacing={1}>
            <Grid item xs={12} lg={6}>
              <TextField
                size="small"
                id="skuFilter"
                label="SKU Filter"
                variant="outlined"
                value={skuFilterInput}
                onChange={e => setSkuFilterInput(e.target.value)}
                placeholder="e.g., SKU001, SKU002, SKU003"
                helperText={`Separated by commas (${filters.skuFilter.length} SKUs)`}
              />
            </Grid>

            <Grid item xs={12} lg={6}>
              <Autocomplete
                size="small"
                id="fromWarehouseZoneId"
                options={warehouseZones}
                getOptionLabel={option => getZoneNameFromZone(option) || option.name}
                value={warehouseZones.find(z => z.id === filters.fromWarehouseZoneId) || null}
                onChange={(_, value) =>
                  setFilters({
                    ...filters,
                    fromWarehouseZoneId: value?.id || null,
                  })
                }
                renderInput={params => (
                  <TextField {...params} label="From Zone" variant="outlined" />
                )}
              />
            </Grid>
          </Grid>

          <Grid container item xs={12} md={6} spacing={1}>
            <Grid item xs={12} lg={6}>
              <Autocomplete
                size="small"
                id="toWarehouseZoneId"
                options={warehouseZones}
                getOptionLabel={option => getZoneNameFromZone(option) || option.name}
                value={warehouseZones.find(z => z.id === filters.toWarehouseZoneId) || null}
                onChange={(_, value) =>
                  setFilters({
                    ...filters,
                    toWarehouseZoneId: value?.id || null,
                  })
                }
                renderInput={params => <TextField {...params} label="To Zone" variant="outlined" />}
              />
            </Grid>
          </Grid>

          <Grid container item xs={12} spacing={1}>
            <Grid item xs={12} md={3}>
              <TextField
                size="small"
                id="dateFrom"
                label="Date From"
                type="date"
                value={filters.dateFrom}
                onChange={e =>
                  setFilters({
                    ...filters,
                    dateFrom: e.target.value,
                  })
                }
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                size="small"
                id="dateTo"
                label="Date To"
                type="date"
                value={filters.dateTo}
                onChange={e =>
                  setFilters({
                    ...filters,
                    dateTo: e.target.value,
                  })
                }
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          item
          xs={4}
          spacing={1}
          direction="column"
          justifyContent="start"
          alignContent="end"
        >
          <Grid item>
            <Button variant="contained" onClick={handleFilterSubmit} ordinality="primary">
              Search
            </Button>
          </Grid>

          <Grid item>
            <Button variant="outlined" onClick={handleFilterClear} ordinality="secondary">
              Clear
            </Button>
          </Grid>

          <Grid item>
            <FormControlLabel
              control={
                <Switch
                  checked={storeSearch}
                  onChange={e => setStoreSearch(e.target.checked)}
                  color="primary"
                />
              }
              label="Remember Search"
            />
          </Grid>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2 }}>
        {loading && !transfers.length ? (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: '400px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <StripedDataGrid
            rows={transfers}
            rowHeight={40}
            rowCount={resultCount}
            loading={loading}
            onRowClick={onRowClick}
            disableDensitySelector
            disableColumnFilter
            disableColumnMenu
            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={model => {
              setPaginationModel(model);
              setSearchInitiated(true);
            }}
            pageSizeOptions={[PAGE_SIZE, PAGE_SIZE * 2, PAGE_SIZE * 4]}
            sortingMode="server"
            sortingOrder={['desc', 'asc', null]}
            sortModel={sortModel}
            onSortModelChange={model => {
              setSortModel(model);
              setSearchInitiated(true);
            }}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            columns={columns}
            slots={{
              toolbar: CustomToolbar,
              footer: CustomPagination,
            }}
          />
        )}
      </Box>
    </PageWrapper>
  );
};

export default InventoryTransfersList;
